import { StoreLoadingEnum, InitialState } from 'src/store/state/types';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { defaultGlobalSettings } from 'lca-common-ui-components';
import { User, UserEntitlements, UserSettings } from './types';
import _ from 'lodash';

import usersApi from './api';
import { RootState } from '../..';
import { createApiError } from 'src/services/api/helper';

/**
 * Initial State
 */

const initialState: InitialState<{
  user: User | null;
  entitlements: UserEntitlements | null;
  settings: UserSettings | null;
}> = {
  loading: StoreLoadingEnum.idle,
  error: null,
  data: {
    user: null,
    entitlements: null,
    settings: null,
  },
};

/**
 * Async Actions
 */

export const fetchUsers = createAsyncThunk('users/fetch', async () => {
  try {
    const response = await usersApi.fetchUser();
    return { data: { ...response.data } };
  } catch (e) {
    console.error(e);
    throw e;
  }
});

export const fetchUsersEntitlements = createAsyncThunk('users/entitlements/fetch', async () => {
  try {
    const response = await usersApi.fetchUserEntitlements();
    return { data: { ...response.data } };
  } catch (e) {
    console.error(e);
  }
});

export const fetchUserSettings = createAsyncThunk('users/config/fetch', async () => {
  try {
    const response = await usersApi.fetchUserSettings();
    return { ...response.data };
  } catch (e) {
    console.error(e);
  }
});

export const updateUserSettings = createAsyncThunk(
  'users/config/update',
  async (options: UserSettings, thunkApi) => {
    try {
      await usersApi.updateUserSettings(options);
    } catch (e) {
      // @ts-ignore
      return thunkApi.rejectWithValue(createApiError(e));
    }
  },
);

export const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = StoreLoadingEnum.pending;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = StoreLoadingEnum.succeeded;
        const { data } = action.payload;
        state.data.user = data;
      })
      .addCase(fetchUsers.rejected, (state) => {
        state.loading = StoreLoadingEnum.failed;
      })
      .addCase(fetchUsersEntitlements.pending, (state) => {
        state.loading = StoreLoadingEnum.pending;
      })
      .addCase(fetchUsersEntitlements.fulfilled, (state, action) => {
        state.loading = StoreLoadingEnum.succeeded;
        const { data } = action.payload!;
        state.data.entitlements = data;
      })
      .addCase(fetchUsersEntitlements.rejected, (state) => {
        state.loading = StoreLoadingEnum.failed;
      })
      .addCase(fetchUserSettings.pending, (state) => {
        state.loading = StoreLoadingEnum.pending;
      })
      .addCase(fetchUserSettings.fulfilled, (state, action) => {
        state.loading = StoreLoadingEnum.succeeded;
        state.data.settings = action.payload ?? null;
        if (!action.payload || _.isEmpty(action.payload)) {
          state.data.settings = defaultGlobalSettings;
        }
      })
      .addCase(fetchUserSettings.rejected, (state) => {
        state.loading = StoreLoadingEnum.failed;
      })
      .addCase(updateUserSettings.pending, (state) => {
        state.loading = StoreLoadingEnum.pending;
      })
      .addCase(updateUserSettings.fulfilled, (state) => {
        state.loading = StoreLoadingEnum.succeeded;
      })
      .addCase(updateUserSettings.rejected, (state) => {
        state.loading = StoreLoadingEnum.failed;
      });
  },
});

export const usersSelector = (state: RootState) => state.users.data;
export const isLoadingSelector = (state: RootState) =>
  state.users.loading === StoreLoadingEnum.pending;

export const userSettingsSelector = (state: RootState) => state.users.data.settings;
